import { Box, Button, CircularProgress, Container, Divider, Grid2, List, ListItem, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { reportingPageInitialLoadAsync, selectReportState, setVoucherSearchKey, voucherLookupAsync } from "./ReportingSlice";
import { RefreshOutlined, SearchOutlined } from "@mui/icons-material";

/* Dates come back from Firestore as Timestamps even though the models type them
   as Date, so handle both. */
function formatDate(value: any): string {
    if (value == null) return "Not set";
    if (typeof value.toDate === "function") return value.toDate().toLocaleString();
    if (value instanceof Date) return value.toLocaleString();
    return `${value}`;
}

function DetailRow(props: { label: string, value: string }) {
    return (
        <Typography margin={1}>
            {props.label}: {props.value}
        </Typography>
    );
}

function WarningRow(props: { text: string }) {
    return (
        <Typography margin={1} sx={{ color: "#D32F2F", fontWeight: "bold" }}>
            {props.text}
        </Typography>
    );
}

export function ReportingPage() {

    const theme = useTheme();
    const lessThanSm = useMediaQuery(theme.breakpoints.down("sm"));
    const dispatch = useAppDispatch();
    const state = useAppSelector(selectReportState)

    useEffect(() => {

    }, []);

    const lookup = state.lookupResult;

    return (
        <Container maxWidth="sm" disableGutters={lessThanSm}>

            <Box
                sx={{
                    height: "100vh",
                    display: state.isLoading ? "flex" : "none",
                    justifyContent: "center",
                    alignItems: "center",
                    alignContent: "center",
                }}
            >
                <Grid2
                    container
                    direction="column"
                    spacing={2}
                    justifyContent="center"
                    alignItems="center"
                >
                    <Grid2>
                        <CircularProgress size={60} />
                    </Grid2>
                    <Grid2>
                        <Typography>Processing</Typography>
                    </Grid2>
                </Grid2>
            </Box>

            <Box
                sx={{
                    minHeight: "100vh",
                    display: state.isLoading ? "none" : "flex",
                    alignItems: "start",
                }}
            >
                <Grid2
                    container
                    spacing={2}
                    direction="column"
                    sx={{ mt: 2 }}
                    columns={{ xs: 6, sm: 6, md: 12 }}
                >
                    <Button
                        component="label"
                        variant="contained"
                        startIcon={<RefreshOutlined sx={{ color: "#FFFFFF" }} />}
                        onClick={(e: any) => {
                            dispatch(reportingPageInitialLoadAsync());
                        }}
                    >
                        <Typography variant="h5">Refresh</Typography>
                    </Button>
                    {state.loadError != null &&
                        <WarningRow text={state.loadError} />
                    }
                    <Typography variant="h2">Number of products: {state.summaries.length}</Typography>
                    <List>{state.summaries.map(((summary, index) => (
                        <ListItem key={index}>
                            <Grid2>
                                <Typography variant="h2">{summary.companyName}</Typography>
                                <Typography variant="h2">{summary.productName}</Typography>
                                <Typography margin={1}>Total Vouchers: {summary.totalVoucherCount}</Typography>
                                <Typography margin={1}>Unexported Vouchers: {summary.unexportedVoucherCount}</Typography>
                                <Typography margin={1}>Unassigned Vouchers: {summary.unassignedVoucherCount}</Typography>
                                <Typography margin={1}>Redeemed Vouchers: {summary.redeemedVoucherCount}</Typography>

                                <Typography margin={1} sx={{ mt: 2 }}>Total Product Keys: {summary.totalProductKeyCount}</Typography>
                                <Typography margin={1}>Assigned Product Keys: {summary.assignedProductKeyCount}</Typography>
                                <Typography margin={1}>Available Product Keys: {summary.redeemableProductKeyCount}</Typography>

                                {summary.invisibleProductKeyCount > 0 &&
                                    <WarningRow text={`${summary.invisibleProductKeyCount} unassigned product key(s) are invisible to redemption because their deletedDate field is missing rather than null.`} />
                                }
                                {summary.redeemableProductKeyCount === 0 && summary.unassignedVoucherCount > 0 &&
                                    <WarningRow text={"No product keys available. Every redemption for this product will fail with \"No vouchers available\" until more keys are imported."} />
                                }
                                {summary.redeemableProductKeyCount > 0 && summary.productKeyShortfall > 0 &&
                                    <WarningRow text={`Short by ${summary.productKeyShortfall} product key(s): ${summary.unassignedVoucherCount} voucher(s) are still outstanding but only ${summary.redeemableProductKeyCount} key(s) remain.`} />
                                }
                            </Grid2>
                        </ListItem>

                    )))}

                    </List>

                    <Divider sx={{ mt: 2, mb: 2 }} />

                    <Typography variant="h2">Voucher lookup</Typography>

                    <Grid2 container spacing={2} direction="row" alignItems="center">
                        <Grid2>
                            <TextField
                                label="Voucher code"
                                value={state.voucherSearchKey}
                                onChange={(e) => {
                                    dispatch(setVoucherSearchKey(e.target.value));
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        dispatch(voucherLookupAsync());
                                    }
                                }}
                            />
                        </Grid2>
                        <Grid2>
                            <Button
                                component="label"
                                variant="contained"
                                startIcon={<SearchOutlined sx={{ color: "#FFFFFF" }} />}
                                onClick={(e: any) => {
                                    dispatch(voucherLookupAsync());
                                }}
                            >
                                <Typography variant="h5">Search</Typography>
                            </Button>
                        </Grid2>
                    </Grid2>

                    <Box
                        sx={{
                            display: state.isLookupLoading ? "flex" : "none",
                            alignItems: "center",
                        }}
                    >
                        <CircularProgress size={30} />
                        <Typography margin={1}>Searching</Typography>
                    </Box>

                    {state.lookupError != null &&
                        <WarningRow text={`Lookup failed: ${state.lookupError}`} />
                    }

                    {!state.isLookupLoading && lookup != null && lookup.voucher == null &&
                        <Typography margin={1}>No voucher found with code {lookup.searchedKey}.</Typography>
                    }

                    {!state.isLookupLoading && lookup != null && lookup.voucher != null &&
                        <Grid2 sx={{ mb: 4 }}>
                            <Typography variant="h2">{lookup.voucher.key}</Typography>

                            {lookup.warnings.map((warning, index) => (
                                <WarningRow key={index} text={warning} />
                            ))}

                            <Typography variant="h3" sx={{ mt: 2 }}>Status</Typography>
                            <DetailRow label="Redeemed" value={lookup.voucher.redeembedDate == null ? "No, still available" : "Yes"} />
                            <DetailRow label="Date redeemed" value={formatDate(lookup.voucher.redeembedDate)} />
                            <DetailRow label="Date exported" value={formatDate(lookup.voucher.exportedDate)} />

                            <Typography variant="h3" sx={{ mt: 2 }}>Company and product</Typography>
                            <DetailRow label="Company" value={lookup.company?.name ?? "Unknown"} />
                            <DetailRow label="Country" value={lookup.company?.isoCountryCode ?? "Unknown"} />
                            <DetailRow label="License prepend code" value={lookup.company?.licensePrependCode ?? "Unknown"} />
                            <DetailRow label="Product" value={lookup.product?.name ?? "Unknown"} />
                            <DetailRow label="Email template" value={lookup.product?.emailTemplate ?? "Not set"} />

                            <Typography variant="h3" sx={{ mt: 2 }}>Redeemed by</Typography>
                            {lookup.redeemedBy == null
                                ? <Typography margin={1}>Nobody has redeemed this voucher.</Typography>
                                : <>
                                    <DetailRow label="Email" value={lookup.redeemedBy.email} />
                                    <DetailRow label="Marketing consent" value={lookup.voucher.marketingConsent ? "Yes" : "No"} />
                                    <DetailRow label="User first seen" value={formatDate(lookup.redeemedBy.createdDate)} />
                                    <DetailRow label="User guid" value={lookup.redeemedBy.guid} />
                                </>
                            }

                            <Typography variant="h3" sx={{ mt: 2 }}>Product key issued</Typography>
                            {lookup.productKey == null
                                ? <Typography margin={1}>No product key is linked to this voucher.</Typography>
                                : <>
                                    <DetailRow label="Key" value={lookup.productKey.key} />
                                    <DetailRow label="Assigned" value={formatDate(lookup.productKey.lastModifiedDate)} />
                                    <DetailRow label="Activation date" value={formatDate(lookup.productKey.activationDate)} />
                                    <DetailRow label="Expiration date" value={formatDate(lookup.productKey.expirationDate)} />
                                    <DetailRow label="Imported" value={formatDate(lookup.productKey.createdDate)} />
                                    {lookup.productKey.transferredDate != null &&
                                        <>
                                            <DetailRow
                                                label="Transferred from"
                                                value={`${lookup.productKeyOriginCompany?.name ?? "Unknown company"} - ${lookup.productKeyOriginProduct?.name ?? "Unknown product"}`} />
                                            <DetailRow label="Date transferred" value={formatDate(lookup.productKey.transferredDate)} />
                                        </>
                                    }
                                    <DetailRow label="Product key guid" value={lookup.productKey.guid} />
                                </>
                            }

                            <Typography variant="h3" sx={{ mt: 2 }}>Record</Typography>
                            <DetailRow label="Voucher guid" value={lookup.voucher.guid} />
                            <DetailRow label="Created" value={formatDate(lookup.voucher.createdDate)} />
                            <DetailRow label="Last modified" value={formatDate(lookup.voucher.lastModifiedDate)} />
                            <DetailRow label="Deleted" value={formatDate(lookup.voucher.deletedDate)} />
                        </Grid2>
                    }
                </Grid2>
            </Box>
        </Container>
    );
}
